import type { FeatureFlagProvider } from './core/featureFlagClient.types';

export type FeatureFlagKind = 'boolean' | 'string' | 'number';

type FeatureFlagValueByKind = {
  boolean: boolean;
  string: string;
  number: number;
};

export type PortalFeatureFlagKey = `frontend_portal_${string}` | `backend_${string}`;

export type PortalFeatureFlagDefinition<
  Provider extends FeatureFlagProvider = FeatureFlagProvider,
  Kind extends FeatureFlagKind = FeatureFlagKind,
> = {
  readonly key: PortalFeatureFlagKey;
  readonly provider: Provider;
  readonly kind: Kind;
};

export type FeatureFlagValue<
  Definition extends PortalFeatureFlagDefinition<FeatureFlagProvider, FeatureFlagKind>,
> = FeatureFlagValueByKind[Definition['kind']];

export type PortalFeatureFlagRegistry = Record<
  string,
  PortalFeatureFlagDefinition<FeatureFlagProvider, FeatureFlagKind>
>;

export function definePortalFeatureFlag<
  const Provider extends FeatureFlagProvider,
  const Name extends string,
  const Kind extends FeatureFlagKind,
>(
  provider: Provider,
  name: Name,
  kind: Kind
): PortalFeatureFlagDefinition<Provider, Kind> & { readonly key: `frontend_portal_${Name}` } {
  return {
    key: `frontend_portal_${name}` as `frontend_portal_${Name}`,
    provider,
    kind,
  };
}

/**
 * Registers a Portal flag with an explicit Split name.
 * Use only for allowlisted backend exceptions that must share a Split flag with
 * the server; normal Portal flags should use `definePortalFeatureFlag`.
 */
export function definePortalFeatureFlagByKey<
  const Provider extends FeatureFlagProvider,
  const Key extends `backend_${string}`,
  const Kind extends FeatureFlagKind,
>(
  provider: Provider,
  key: Key,
  kind: Kind
): PortalFeatureFlagDefinition<Provider, Kind> & { readonly key: Key } {
  return {
    key,
    provider,
    kind,
  };
}
